import React from 'react';
import { Link } from 'react-router-dom';
import { Upload, Download, Lock, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatEuro, formatDate } from '@/lib/format';
import { IMPORT_TYPES, downloadTemplate } from '@/lib/estimating/templates';
import { EST_IDS } from '@/constants/testIds/estimating';

export function StatusBadge({ status }) {
  const map = {
    draft: 'bg-slate-100 text-slate-700',
    confirmed: 'bg-emerald-100 text-emerald-800',
    superseded: 'bg-muted text-muted-foreground',
    rejected: 'bg-red-100 text-red-800',
  };
  return <Badge className={`${map[status] || map.draft} border-0 capitalize`}>{status}</Badge>;
}

export default function ImportTypeCard({ type, imports, projectId, canImport, canView, onImport }) {
  const t = IMPORT_TYPES[type];
  const confirmed = imports.find((i) => i.status === 'confirmed');
  const history = imports.filter((i) => i.id !== confirmed?.id);
  const headline = confirmed
    ? type === 'tender_summary' ? confirmed.tender_total_inc_vat : confirmed.computed_total
    : null;

  return (
    <div data-testid={EST_IDS.typeCard(type)} className="rounded-lg border border-border bg-white flex flex-col">
      <div className="px-5 pt-5 pb-4 border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display font-semibold text-lg text-charcoal flex items-center gap-2">
              {t.label} {t.restricted && <Lock size={14} className="text-muted-foreground" />}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">Visible to: {t.visibility}</div>
          </div>
          {confirmed && <StatusBadge status="confirmed" />}
        </div>
        {!canView ? (
          <div className="mt-4 text-sm text-muted-foreground">Restricted to owner, admin and manager roles.</div>
        ) : confirmed ? (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {type === 'tender_summary' ? 'Tender total incl. VAT' : type === 'priced_boq' ? 'Selling total ex VAT' : 'Estimated direct cost'}
            </div>
            <div className="font-display font-bold text-2xl text-charcoal tabular-nums">{formatEuro(headline)}</div>
            <div className="text-xs text-muted-foreground mt-1">
              v{confirmed.version_number} · {confirmed.row_count} lines · confirmed {formatDate(confirmed.confirmed_at)}
              {confirmed.reconciled ? ' · reconciled' : ' · override on record'}
            </div>
            {type === 'tender_summary' && (
              <div className="text-xs text-muted-foreground">Ex VAT {formatEuro(confirmed.tender_subtotal_ex_vat)} · VAT {formatEuro(confirmed.tender_vat_amount)}</div>
            )}
            <Link to={`/projects/${projectId}/estimating/${confirmed.id}`} className="text-xs text-bronze hover:underline mt-1 inline-block">View lines →</Link>
          </div>
        ) : (
          <div className="mt-4 text-sm text-muted-foreground">No confirmed version yet. {t.description}</div>
        )}
      </div>

      {canView && history.length > 0 && (
        <div className="px-5 py-3 border-b border-border">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><History size={12} /> Version history</div>
          <ul className="space-y-1">
            {history.map((i) => (
              <li key={i.id} data-testid={EST_IDS.versionRow} className="flex items-center justify-between text-xs">
                <Link to={`/projects/${projectId}/estimating/${i.id}`} className="text-charcoal hover:text-bronze">v{i.version_number} · {formatDate(i.uploaded_at)}</Link>
                <span className="flex items-center gap-2 tabular-nums text-muted-foreground">
                  {i.status === 'superseded' && formatEuro(type === 'tender_summary' ? i.tender_total_inc_vat : i.computed_total)}
                  <StatusBadge status={i.status} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="px-5 py-3 mt-auto flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" data-testid={EST_IDS.templateButton(type)} onClick={() => downloadTemplate(type)}>
          <Download size={14} className="mr-1" /> Template
        </Button>
        {canImport && (
          <Button type="button" size="sm" data-testid={EST_IDS.importButton(type)} onClick={() => onImport(type)} className="bg-bronze hover:bg-bronze-hover text-white">
            <Upload size={14} className="mr-1" /> {confirmed ? 'Import new version' : 'Import'}
          </Button>
        )}
      </div>
    </div>
  );
}
